import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AccessDenied from "../../../components/access_denied/access_denied";
import CmsLayout from "../../../layouts/cms/cms_layout";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import { deleteNews, getNewsList } from "../../../services/cms/cms";
import DeleteCmsItemDialog from "../components/delete_cms_item/delete_cms_item";
import FilterNews, { NewsFilterValues } from "../components/filter_news/filter_news";
import NewsListing, { StatusFilter } from "../components/news_listing/news_listing";
import SortNewsPopup, {
  DEFAULT_NEWS_SORT_OPTION,
  NewsSortOption,
  newsSortOptionLabel,
  newsSortOptionToParams,
} from "../components/sort_news_popup/sort_news_popup";
import { News } from "../models/cms";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";

const ROWS_PER_PAGE = 10;

export default function NewsPage() {
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<NewsSortOption>(DEFAULT_NEWS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<News | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, statusFilter, dateFrom, dateTo, selectedOrgId, sortOption]);

  const fromDate = dateFrom?.format("YYYY-MM-DD");
  const toDate = dateTo?.format("YYYY-MM-DD");

  const { data, isLoading, isFetching } = useQuery(
    ["cms-news", page, debouncedSearchValue, statusFilter, selectedOrgId, sortOption, fromDate, toDate],
    async () => {
      const { sortBy, sortOrder } = newsSortOptionToParams(sortOption);
      const response = await getNewsList({
        page,
        size: ROWS_PER_PAGE,
        search: debouncedSearchValue,
        status: statusFilter === "all" ? undefined : statusFilter,
        orgId: selectedOrgId || undefined,
        sortBy,
        sortOrder,
        fromDate,
        toDate,
      });
      return response?.data?.data;
    },
    { keepPreviousData: true }
  );

  const list: News[] = data?.list || [];
  const count = data?.total || 0;

  const filterCount = [statusFilter !== "all", !!dateFrom && !!dateTo].filter(Boolean).length;

  const handleApplyFilters = (values: NewsFilterValues) => {
    setStatusFilter(values.statusFilter);
    setDateFrom(values.dateFrom);
    setDateTo(values.dateTo);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteNews(deleteTarget.id)
      .then(() => {
        showSnackbar("News deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-news"] });
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => {
        setIsDeleting(false);
        setDeleteTarget(null);
      });
  };

  return (
    <MainLayout>
      {hasPermission("cms", "read") ? (
        <CmsLayout>
          <NewsListing
            list={list}
            count={count}
            page={page}
            setPage={setPage}
            isLoading={isLoading || isFetching}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filterCount={filterCount}
            onOpenFilters={() => setFilterDialogOpen(true)}
            sortLabel={newsSortOptionLabel(sortOption)}
            onOpenSort={() => setSortDialogOpen(true)}
            onDelete={setDeleteTarget}
          />
          <DeleteCmsItemDialog
            open={!!deleteTarget}
            itemName={deleteTarget?.title}
            isDeleting={isDeleting}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
          {filterDialogOpen && (
            <FilterNews
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              statusFilter={statusFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={handleApplyFilters}
            />
          )}
          <SortNewsPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </CmsLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
    </MainLayout>
  );
}
